import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { useWizard } from "../../context/WizardContext";
import { MaskedSsnInput } from "../shared/MaskedSsnInput";
import { BUSINESS_TYPE, US_STATE_CODES, ACCOUNTING_METHOD } from "../../types/wizard.types";
import type {
  BusinessDetails as BusinessDetailsType,
  PartnershipDetails as PartnershipDetailsType,
  PartnerInfo as PartnerInfoType,
} from "../../types/business.types";
import { cn } from "@/lib/utils";

const BUSINESS_TYPE_LABELS: Record<string, string> = {
  [BUSINESS_TYPE.SOLE_PROPRIETOR]: "Sole Proprietor",
  [BUSINESS_TYPE.SINGLE_MEMBER_LLC]: "Single Member LLC",
  [BUSINESS_TYPE.PARTNERSHIP]: "Partnership",
  [BUSINESS_TYPE.S_CORP]: "S-Corp",
  [BUSINESS_TYPE.C_CORP]: "C-Corp",
};

const PARTNERSHIP_TYPES = [BUSINESS_TYPE.PARTNERSHIP, BUSINESS_TYPE.S_CORP];

export function BusinessStep1Details() {
  const { businessData, updateBusinessData } = useWizard();
  const d = businessData.businessDetails ?? ({} as BusinessDetailsType);
  const partnership = businessData.partnershipDetails ?? ({} as PartnershipDetailsType);
  const partners = partnership.partnerInformation ?? [];
  const numPartners = partnership.numberOfPartners ?? 0;
  const showPartnership = PARTNERSHIP_TYPES.includes(d.businessType as (typeof PARTNERSHIP_TYPES)[number]);

  const set = (patch: Partial<BusinessDetailsType>) => {
    updateBusinessData({ businessDetails: { ...d, ...patch } });
  };

  const setAddress = (patch: Partial<NonNullable<BusinessDetailsType["businessAddress"]>>) => {
    set({ businessAddress: { ...d.businessAddress, ...patch } });
  };

  const setTaxpayerId = (patch: Partial<NonNullable<BusinessDetailsType["taxpayerId"]>>) => {
    set({ taxpayerId: { ...d.taxpayerId, idType: "SSN", idValue: "", ...d.taxpayerId, ...patch } });
  };

  const setPartnership = (patch: Partial<PartnershipDetailsType>) => {
    updateBusinessData({
      partnershipDetails: { ...partnership, ...patch },
    });
  };

  const setNumberOfPartners = (n: number) => {
    const safe = Math.max(0, Math.min(50, n));
    const current = partnership.partnerInformation ?? [];
    const next: PartnerInfoType[] = Array.from({ length: safe }, (_, i) => current[i] ?? {});
    setPartnership({ numberOfPartners: safe, partnerInformation: next });
  };

  const setPartner = (index: number, patch: Partial<PartnerInfoType>) => {
    const list = [...partners];
    list[index] = { ...list[index], ...patch };
    setPartnership({ partnerInformation: list });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Business Details</h2>
        <p className="text-sm text-muted-foreground">
          Enter your business and proprietor information.
        </p>
      </div>

      {/* Business Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Business Info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Type of Business <span className="text-destructive">*</span></Label>
            <Select
              value={d.businessType ?? ""}
              onValueChange={(v) => set({ businessType: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(BUSINESS_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="businessName">Business Name <span className="text-destructive">*</span></Label>
            <Input
              id="businessName"
              value={d.businessName ?? ""}
              onChange={(e) => set({ businessName: e.target.value })}
              placeholder="Legal business name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="principalBusiness">Principal Business or Profession <span className="text-destructive">*</span></Label>
            <Input
              id="principalBusiness"
              value={d.principalBusiness ?? ""}
              onChange={(e) => set({ principalBusiness: e.target.value })}
              placeholder="e.g. Consulting"
            />
          </div>
          <div className="space-y-2">
            <Label>Accounting Method <span className="text-destructive">*</span></Label>
            <RadioGroup
              value={d.accountingMethod ?? ACCOUNTING_METHOD.CASH}
              onValueChange={(v) => set({ accountingMethod: v as "CASH" | "ACCRUAL" })}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={ACCOUNTING_METHOD.CASH} id="acc-cash" />
                <Label htmlFor="acc-cash" className="font-normal">Cash</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={ACCOUNTING_METHOD.ACCRUAL} id="acc-accrual" />
                <Label htmlFor="acc-accrual" className="font-normal">Accrual</Label>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      {/* Proprietor Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Proprietor Info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="proprietorName">Name of Business Owner <span className="text-destructive">*</span></Label>
            <Input
              id="proprietorName"
              value={d.proprietorName ?? ""}
              onChange={(e) => set({ proprietorName: e.target.value })}
              placeholder="Full name"
            />
          </div>
          <MaskedSsnInput
            label="SSN"
            value={d.taxpayerId?.idValue ?? ""}
            onChange={(v) => setTaxpayerId({ idType: "SSN", idValue: v })}
          />
        </CardContent>
      </Card>

      {/* Business Address */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Business Address (required)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="street">Street <span className="text-destructive">*</span></Label>
            <Input
              id="street"
              value={d.businessAddress?.street ?? ""}
              onChange={(e) => setAddress({ street: e.target.value })}
              placeholder="Street address"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="city">City <span className="text-destructive">*</span></Label>
              <Input
                id="city"
                value={d.businessAddress?.city ?? ""}
                onChange={(e) => setAddress({ city: e.target.value })}
                placeholder="City"
              />
            </div>
            <div className="space-y-2">
              <Label>State <span className="text-destructive">*</span></Label>
              <Select
                value={d.businessAddress?.state ?? ""}
                onValueChange={(v) => setAddress({ state: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATE_CODES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="zip">ZIP <span className="text-destructive">*</span></Label>
              <Input
                id="zip"
                value={d.businessAddress?.zip ?? ""}
                onChange={(e) => setAddress({ zip: e.target.value })}
                placeholder="ZIP"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value="USA"
                readOnly
                className="bg-muted"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Partnership (1065 / 1120-S) */}
      {showPartnership && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Partnership Details</CardTitle>
            <p className="text-sm text-muted-foreground">
              Number of partners and partner information (Partnership 1065 / S-Corp 1120-S).
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="numberOfPartners">Number of Partners <span className="text-destructive">*</span></Label>
              <Input
                id="numberOfPartners"
                type="number"
                min={0}
                max={50}
                value={numPartners > 0 ? numPartners : ""}
                placeholder="0"
                onChange={(e) => {
                  const v = e.target.value ? parseInt(e.target.value, 10) : 0;
                  setNumberOfPartners(Number.isNaN(v) ? 0 : v);
                }}
              />
            </div>
            {numPartners > 0 && (
              <div className="space-y-6">
                {partners.slice(0, numPartners).map((partner, index) => (
                  <div
                    key={index}
                    className={cn("rounded-lg border border-border p-4 space-y-4", index > 0 && "mt-4")}
                  >
                    <p className="text-sm font-medium text-foreground">Partner {index + 1} (required)</p>
                    <div className="space-y-2">
                      <Label htmlFor={`partner-name-${index}`}>Name <span className="text-destructive">*</span></Label>
                      <Input
                        id={`partner-name-${index}`}
                        value={partner.name ?? ""}
                        onChange={(e) => setPartner(index, { name: e.target.value })}
                        placeholder="Full name"
                      />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor={`partner-street-${index}`}>Street <span className="text-destructive">*</span></Label>
                        <Input
                          id={`partner-street-${index}`}
                          value={partner.street ?? partner.address ?? ""}
                          onChange={(e) =>
                            setPartner(index, { street: e.target.value, address: e.target.value })
                          }
                          placeholder="Street address"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`partner-city-${index}`}>City <span className="text-destructive">*</span></Label>
                        <Input
                          id={`partner-city-${index}`}
                          value={partner.city ?? ""}
                          onChange={(e) => setPartner(index, { city: e.target.value })}
                          placeholder="City"
                        />
                      </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>State <span className="text-destructive">*</span></Label>
                        <Select
                          value={partner.state ?? ""}
                          onValueChange={(v) => setPartner(index, { state: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="State" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATE_CODES.map((code) => (
                              <SelectItem key={code} value={code}>
                                {code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`partner-zip-${index}`}>ZIP <span className="text-destructive">*</span></Label>
                        <Input
                          id={`partner-zip-${index}`}
                          value={partner.zip ?? ""}
                          onChange={(e) => setPartner(index, { zip: e.target.value })}
                          placeholder="ZIP"
                        />
                      </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <MaskedSsnInput
                          label="SSN"
                          value={partner.ssn ?? ""}
                          onChange={(v) => setPartner(index, { ssn: v })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`partner-pct-${index}`}>Partnership Percentage <span className="text-destructive">*</span></Label>
                        <Input
                          id={`partner-pct-${index}`}
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          value={partner.partnershipPercentage ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setPartner(
                              index,
                              { partnershipPercentage: v ? Number(v) : undefined }
                            );
                          }}
                          placeholder="e.g. 50"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Additional */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Additional</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Materially participate in this business?</Label>
            <RadioGroup
              value={d.materiallyParticipated === true ? "yes" : d.materiallyParticipated === false ? "no" : ""}
              onValueChange={(v) => set({ materiallyParticipated: v === "yes" })}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="mat-yes" />
                <Label htmlFor="mat-yes" className="font-normal">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="mat-no" />
                <Label htmlFor="mat-no" className="font-normal">No</Label>
              </div>
            </RadioGroup>
          </div>
          <Separator />
          <div className="space-y-2">
            <Label>Did you start or acquire this business this year?</Label>
            <RadioGroup
              value={d.startedThisYear === true ? "yes" : d.startedThisYear === false ? "no" : ""}
              onValueChange={(v) => set({ startedThisYear: v === "yes" })}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="start-yes" />
                <Label htmlFor="start-yes" className="font-normal">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="start-no" />
                <Label htmlFor="start-no" className="font-normal">No</Label>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
